import React, { useContext, useEffect, useRef, useState } from "react";
import LoadingIcon from "../../images/Loading_icon.gif";
import industryLogo from "../../images/default_industry.png";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  differenceInMonths,
  endOfMonth,
  parse,
  startOfMonth,
} from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from "lucide-react";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const YEARS = Array.from(
  { length: 60 },
  (_, index) => new Date().getFullYear() - index
);

const MONTH_YEAR_PATTERN =
  /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}$/;
const YEAR_ONLY_PATTERN = /^\d{4}$/;

function resolveExperienceLogo(value) {
  const logo = String(value || "").trim();

  if (!logo) return industryLogo;

  return logo.includes("/pictures/")
    ? `https://images.expertpowerhouse.com${logo}`
    : logo;
}

function buildExperienceDate(month, year) {
  return [month, year].filter(Boolean).join(" ").trim();
}

function splitExperienceDate(value) {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return {
      month: "",
      year: "",
      isCurrent: false,
    };
  }

  if (["present", "heute"].includes(cleanValue.toLowerCase())) {
    return {
      month: "",
      year: "",
      isCurrent: true,
    };
  }

  if (YEAR_ONLY_PATTERN.test(cleanValue)) {
    return {
      month: "",
      year: cleanValue,
      isCurrent: false,
    };
  }

  if (MONTH_YEAR_PATTERN.test(cleanValue)) {
    const [month, year] = cleanValue.split(" ");

    return {
      month,
      year,
      isCurrent: false,
    };
  }

  const parts = cleanValue.split(/\s+/);

  return {
    month: MONTHS.includes(parts[0]) ? parts[0] : "",
    year: parts.find((part) => YEAR_ONLY_PATTERN.test(part)) || "",
    isCurrent: false,
  };
}

function parseExperienceBoundary(value, boundary = "start") {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) return null;

  if (["present", "heute"].includes(cleanValue.toLowerCase())) {
    return endOfMonth(new Date());
  }

  if (YEAR_ONLY_PATTERN.test(cleanValue)) {
    const year = Number(cleanValue);

    // Requested year-only rule:
    // start "2014" => Jan 2014
    // end   "2014" => Dec 2014
    return boundary === "end"
      ? endOfMonth(new Date(year, 11, 1))
      : startOfMonth(new Date(year, 0, 1));
  }

  if (MONTH_YEAR_PATTERN.test(cleanValue)) {
    const parsedDate = parse(cleanValue, "MMM yyyy", new Date());

    return boundary === "end"
      ? endOfMonth(parsedDate)
      : startOfMonth(parsedDate);
  }

  return null;
}

function calculateDurationFromDates(joiningDate, leavingDate) {
  const startDate = parseExperienceBoundary(joiningDate, "start");
  const endDate = parseExperienceBoundary(leavingDate, "end");

  if (!startDate || !endDate || startDate > endDate) return "";

  const totalMonths = differenceInMonths(endDate, startDate) + 1;

  if (totalMonths <= 0) return "";

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return [
    years > 0 ? `${years} yr${years > 1 ? "s" : ""}` : "",
    months > 0 ? `${months} mo${months > 1 ? "s" : ""}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function calculateOverallDuration(blocks = []) {
  const starts = blocks
    .map((block) => parseExperienceBoundary(block?.joiningDate, "start"))
    .filter(Boolean);

  const ends = blocks
    .map((block) => parseExperienceBoundary(block?.leavingDate, "end"))
    .filter(Boolean);

  if (!starts.length || !ends.length) return "";

  const earliest = new Date(
    Math.min(...starts.map((date) => date.getTime()))
  );
  const latest = new Date(
    Math.max(...ends.map((date) => date.getTime()))
  );

  if (earliest > latest) return "";

  const totalMonths = differenceInMonths(latest, earliest) + 1;
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return [
    years > 0 ? `${years} yr${years > 1 ? "s" : ""}` : "",
    months > 0 ? `${months} mo${months > 1 ? "s" : ""}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

const EXPERIENCE_LOGO_DRAG_TYPE =
  "application/x-eph-experience-logo";
const EXPERIENCE_LOGO_TEXT_PREFIX = "EPH_EXPERIENCE_LOGO:";

function parseExperienceLogoDragPayload(rawValue) {
  if (!rawValue) return null;

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    try {
      return JSON.parse(decodeURIComponent(rawValue));
    } catch (decodeError) {
      return null;
    }
  }
}

function handleExperienceLogoDragStart(event, value) {
  const imageValue = String(value || "").trim();
  const imageUrl = resolveExperienceLogo(imageValue);

  if (!imageUrl || !event.dataTransfer) return;

  const payload = {
    source: "ExpertPowerPointExperience",
    imageValue,
    imageUrl,
  };

  const payloadJson = JSON.stringify(payload);
  const encodedPayload = encodeURIComponent(payloadJson);

  event.stopPropagation();
  event.dataTransfer.effectAllowed = "copy";

  try {
    event.dataTransfer.setData(EXPERIENCE_LOGO_DRAG_TYPE, payloadJson);
  } catch (error) {}

  try {
    event.dataTransfer.setData(
      "text/html",
      `<img src="${imageUrl}" data-eph-experience-logo="${encodedPayload}" alt="Company logo" />`
    );
  } catch (error) {}

  try {
    event.dataTransfer.setData(
      "text/plain",
      `${EXPERIENCE_LOGO_TEXT_PREFIX}${encodedPayload}`
    );
  } catch (error) {}

  try {
    event.dataTransfer.setData("text/uri-list", imageUrl);
  } catch (error) {}
}

function ExperienceCompanyLogoEditor({ value, onChange }) {
  const fileInputRef = useRef(null);
  const [previewImage, setPreviewImage] = useState("");

  useEffect(() => {
    if (!value) {
      setPreviewImage("");
    }
  }, [value]);

  const previewFile = (file) => {
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      const result = reader.result || "";
      setPreviewImage(result);
      onChange(result);
    };

    reader.readAsDataURL(file);
  };

  const applyCopiedLogoPayload = (payload) => {
    if (!payload) return false;

    const imageValue = String(payload.imageValue || "").trim();
    const imageUrl = String(payload.imageUrl || "").trim();
    const valueToSave = imageValue || imageUrl;
    const valueToPreview =
      imageUrl || resolveExperienceLogo(imageValue);

    if (!valueToSave || !valueToPreview) return false;

    /*
     * Important:
     * - valueToSave keeps the original app/database logo value.
     * - valueToPreview uses the fully resolved image URL.
     *
     * This is the same separation we used for the profile-picture fix:
     * the drop can be accepted/saved even when the stored value itself is
     * not directly browser-displayable.
     */
    setPreviewImage(valueToPreview);
    onChange(valueToSave);

    return true;
  };

  const loadImageUrl = async (imageURL) => {
    const cleanURL = String(imageURL || "").trim();

    if (!cleanURL) return false;

    if (
      cleanURL.includes("images.expertpowerhouse.com") ||
      cleanURL.startsWith("data:image") ||
      cleanURL.startsWith("blob:")
    ) {
      setPreviewImage(cleanURL);
      onChange(cleanURL);
      return true;
    }

    try {
      const response = await fetch(cleanURL, { mode: "cors" });

      if (!response.ok) {
        throw new Error("Could not load image");
      }

      previewFile(await response.blob());
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const dataTransfer = event.dataTransfer;

    const directPayload = parseExperienceLogoDragPayload(
      dataTransfer?.getData(EXPERIENCE_LOGO_DRAG_TYPE)
    );

    if (applyCopiedLogoPayload(directPayload)) return;

    const droppedHtml = dataTransfer?.getData("text/html") || "";

    if (droppedHtml) {
      const documentFragment = new DOMParser().parseFromString(
        droppedHtml,
        "text/html"
      );
      const appLogo = documentFragment.querySelector(
        "img[data-eph-experience-logo]"
      );
      const encodedPayload = appLogo?.getAttribute(
        "data-eph-experience-logo"
      );

      if (
        applyCopiedLogoPayload(
          parseExperienceLogoDragPayload(encodedPayload)
        )
      ) {
        return;
      }
    }

    const droppedPlainText =
      dataTransfer?.getData("text/plain") || "";

    if (droppedPlainText.startsWith(EXPERIENCE_LOGO_TEXT_PREFIX)) {
      const encodedPayload = droppedPlainText.slice(
        EXPERIENCE_LOGO_TEXT_PREFIX.length
      );

      if (
        applyCopiedLogoPayload(
          parseExperienceLogoDragPayload(encodedPayload)
        )
      ) {
        return;
      }
    }

    const droppedFile =
      Array.from(dataTransfer?.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .find(Boolean) ||
      Array.from(dataTransfer?.files || [])[0];

    if (droppedFile) {
      previewFile(droppedFile);
      return;
    }

    if (droppedHtml) {
      const documentFragment = new DOMParser().parseFromString(
        droppedHtml,
        "text/html"
      );
      const imageURL =
        documentFragment.querySelector("img")?.getAttribute("src");

      if (imageURL && (await loadImageUrl(imageURL))) return;
    }

    const droppedUri =
      dataTransfer?.getData("text/uri-list") ||
      droppedPlainText ||
      "";

    if (/^(data:image|blob:|https?:\/\/)/i.test(droppedUri.trim())) {
      const loaded = await loadImageUrl(
        droppedUri.trim().split(/\r?\n/)[0]
      );
      if (loaded) return;
    }

    Swal.fire({
      title: "Image not detected",
      text: "Drop an image file, an image from the web, or a logo from another experience block.",
      icon: "info",
      showConfirmButton: false,
      timer: 1600,
    });
  };

  const handlePaste = async (event) => {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const imageItem = Array.from(clipboardData.items || []).find(
      (item) =>
        item.kind === "file" &&
        String(item.type || "").toLowerCase().startsWith("image/")
    );

    if (imageItem) {
      event.preventDefault();
      event.stopPropagation();
      previewFile(imageItem.getAsFile());
      return;
    }

    const html = clipboardData.getData("text/html") || "";

    if (html) {
      const documentFragment = new DOMParser().parseFromString(
        html,
        "text/html"
      );
      const appLogo = documentFragment.querySelector(
        "img[data-eph-experience-logo]"
      );

      if (appLogo) {
        const encodedPayload = appLogo.getAttribute(
          "data-eph-experience-logo"
        );

        if (
          applyCopiedLogoPayload(
            parseExperienceLogoDragPayload(encodedPayload)
          )
        ) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      const imageURL =
        documentFragment.querySelector("img")?.getAttribute("src");

      if (imageURL) {
        event.preventDefault();
        event.stopPropagation();
        if (await loadImageUrl(imageURL)) return;
      }
    }

    const plainText = (
      clipboardData.getData("text/plain") || ""
    ).trim();

    if (plainText.startsWith(EXPERIENCE_LOGO_TEXT_PREFIX)) {
      const encodedPayload = plainText.slice(
        EXPERIENCE_LOGO_TEXT_PREFIX.length
      );

      if (
        applyCopiedLogoPayload(
          parseExperienceLogoDragPayload(encodedPayload)
        )
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }

    if (/^(data:image|blob:|https?:\/\/)/i.test(plainText)) {
      event.preventDefault();
      event.stopPropagation();
      if (await loadImageUrl(plainText)) return;
    }

    Swal.fire({
      title: "No image found",
      text: "Copy the image itself, move the pointer over the logo box, then press Ctrl/Cmd+V.",
      icon: "info",
      showConfirmButton: false,
      timer: 1700,
    });
  };

  return (
    <div
      className="flex w-[92px] shrink-0 flex-col items-center gap-1.5"
      onPasteCapture={handlePaste}
    >
      <div
        tabIndex={0}
        className="relative flex h-[104px] w-[92px] cursor-pointer flex-col items-center justify-end overflow-hidden rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-2 pb-2 text-center text-slate-400 transition hover:border-[#365561]/35 hover:bg-[#365561]/5 focus:border-[#365561]/45 focus:outline-none"
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
        }}
        onDrop={handleDrop}
        onPaste={handlePaste}
        onMouseEnter={(event) => event.currentTarget.focus()}
        onClick={() => fileInputRef.current?.click()}
        title="Drop, paste or browse a company logo"
      >
        <div className="absolute top-2 flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm">
          {value ? (
            <img
              src={
                previewImage || resolveExperienceLogo(value)
              }
              alt="Company logo"
              draggable
              onDragStart={(event) =>
                handleExperienceLogoDragStart(event, value)
              }
              className="h-full w-full object-contain"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = industryLogo;
              }}
            />
          ) : (
            <Building2 className="h-6 w-6 text-slate-300" />
          )}
        </div>

        <div className="flex items-center gap-1 text-[9px] font-medium">
          <Upload className="h-3 w-3" />
          <span>{value ? "Change" : "Add logo"}</span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => previewFile(event.target.files?.[0])}
        />
      </div>

      {value && (
        <button
          type="button"
          className="text-[10px] font-medium text-rose-500 transition hover:text-rose-700 focus:outline-none"
          onClick={() => {
            setPreviewImage("");
            onChange("");
          }}
        >
          Remove
        </button>
      )}
    </div>
  );
}

export default function ExpertPowerPointExperience({
  expert_id,
  powerPointID,
  readOnly = false,
  initialExperienceData = null,
}) {
  const { API_URL } = useContext(UrlContext);

  const [loading, setLoading] = useState(false);
  const [experienceData, setExperienceData] = useState(() =>
    Array.isArray(initialExperienceData) ? initialExperienceData : []
  );
  const [isModify, setIsModify] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [refresh, setRefresh] = useState(false);

  const saveOrdering = async (newOrder) => {
    if (readOnly) return;
    setRefresh(true);

    try {
      const response = await fetch(
        `${API_URL}power-point-experience-manual-ordering`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            newExperienceOrder: newOrder,
          }),
        }
      );

      const responseData = await response.json();

      if (responseData.status !== "success") {
        console.log("reording error!");
      }
    } catch (error) {
      console.log("reording error!");
    } finally {
      setRefresh(false);
    }
  };

  const onDragEnd = async (result) => {
    if (readOnly) return;
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const reorderedItems = Array.from(experienceData);
    const [removed] = reorderedItems.splice(source.index, 1);
    reorderedItems.splice(destination.index, 0, removed);

    setExperienceData(reorderedItems);

    const newOrder = reorderedItems.map((item, index) => ({
      powerPointExperienceID: item.experienceID,
      index: index + 1,
    }));

    await saveOrdering(newOrder);
  };

  const getExpertExperience = async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}fetch-power-point-experience`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expertID: expert_id,
            powerPointID,
          }),
        }
      );

      const responseData = await response.json();

      if (responseData.status === "success") {
        setExperienceData(responseData.expertExperience || []);
      } else {
        Swal.fire({
          title: "Error!",
          text:
            responseData.text ||
            "Failed to fetch expert experience",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: String(error),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshPowerPointExperience = async () => {
    if (readOnly) return;
    setRefresh(true);

    try {
      const response = await fetch(
        `${API_URL}refresh-power-point-experience`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expertID: expert_id,
            powerPointID,
          }),
        }
      );

      const responseData = await response.json();

      if (responseData.status === "success") {
        await getExpertExperience();
      } else {
        Swal.fire({
          title: "Error!",
          text:
            responseData.text ||
            "Failed to refresh experience",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: String(error),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setRefresh(false);
    }
  };

  const saveRelevantExperience = async (
    experience,
    checkboxValue
  ) => {
    if (readOnly) return;
    try {
      const response = await fetch(
        `${API_URL}add-relevant-experience`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expertID: expert_id,
            experience,
            checkboxValue,
          }),
        }
      );

      const responseData = await response.json();

      if (responseData.status !== "success") {
        Swal.fire({
          title: "Error!",
          text: "Failed to add relevant experience!",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: String(error),
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleCheckboxClick = (experienceID, checked) => {
    setExperienceData((previous) =>
      previous.map((experience) =>
        experience.experienceID === experienceID
          ? {
              ...experience,
              relevantExperience: checked,
            }
          : experience
      )
    );
  };

  useEffect(() => {
    if (Array.isArray(initialExperienceData)) {
      setExperienceData(initialExperienceData);
      setLoading(false);
      return;
    }

    getExpertExperience();
  }, [expert_id, powerPointID, initialExperienceData]);

  return (
    <div className="relative h-full min-h-0 text-[#365460]">
      <style>{`
        .pdp2-experience-section .pdp2-exp-actions {
          right: 0 !important;
          transition: right 150ms ease !important;
        }

        .pdp2-experience-section:hover .pdp2-exp-actions,
        .pdp2-experience-section:focus-within .pdp2-exp-actions {
          right: 30px !important;
        }

        @media print {
          .pdp2-experience-section .pdp2-exp-actions {
            display: none !important;
          }
        }
      `}</style>

      {!readOnly && (
      <div
        className="pdp2-exp-actions"
        data-pdp2-print-hide="true"
      >
        <button
          type="button"
          className="pdp2-exp-action-button"
          onClick={refreshPowerPointExperience}
          disabled={refresh}
          title="Refresh experience from consultant profile"
          aria-label="Refresh experience from consultant profile"
        >
          <RefreshCw
            size={15}
            strokeWidth={1.9}
            className={refresh ? "pdp2-exp-action-spin" : ""}
          />
        </button>

        <button
          type="button"
          className="pdp2-exp-action-button"
          onClick={() => {
            setSelectedExperience(null);
            setIsModify(false);
            setIsNew(true);
          }}
          title="Add a new experience block"
          aria-label="Add a new experience block"
        >
          <Plus size={16} strokeWidth={2} />
        </button>
      </div>
      )}

      {!loading ? (
        <div className="h-full min-h-0 overflow-y-auto pr-1">
          {experienceData.length > 0 ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="experiences">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-0"
                  >
                    {experienceData.map((experience, index) => (
                      <Draggable
                        key={experience.experienceID || index}
                        draggableId={String(
                          experience.experienceID ||
                            `experience-${index}`
                        )}
                        index={index}
                        isDragDisabled={readOnly}
                      >
                        {(provided, snapshot) => {
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              data-pdp2-experience-row="true"
                              className={[
                                "relative select-none rounded-lg px-0 py-1.5 transition duration-200",
                                snapshot.isDragging
                                  ? "cursor-grabbing bg-slate-100 shadow-md"
                                  : readOnly
                                    ? "cursor-default"
                                    : "cursor-grab hover:bg-slate-50",
                              ].join(" ")}
                              onClick={() => {
                                if (readOnly) return;
                                setSelectedExperience(experience);
                                setIsNew(false);
                                setIsModify(true);
                              }}
                              style={provided.draggableProps.style}
                            >
                              {!readOnly && (
                              <input
                                className="absolute right-1 top-1 h-3.5 w-3.5 cursor-pointer accent-[#365561]"
                                data-pdp2-print-hide="true"
                                type="checkbox"
                                checked={Boolean(
                                  experience.relevantExperience
                                )}
                                onClick={(event) => {
                                  event.stopPropagation();

                                  saveRelevantExperience(
                                    experience,
                                    event.target.checked
                                  );

                                  handleCheckboxClick(
                                    experience.experienceID,
                                    event.target.checked
                                  );
                                }}
                                title="Relevant experience"
                                aria-label="Relevant experience"
                              />
                              )}

                              {experience.subExperienceBlocks
                                ?.length > 0 ? (
                                <>
                                  <div className="flex items-start gap-3 pr-5">
                                    <img
                                      src={resolveExperienceLogo(
                                        experience.companyLogo
                                      )}
                                      alt="Company logo"
                                      draggable
                                      onMouseDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onDragStart={(event) =>
                                        handleExperienceLogoDragStart(
                                          event,
                                          experience.companyLogo
                                        )
                                      }
                                      className="h-10 w-10 shrink-0 cursor-grab rounded-full border border-slate-200 bg-white object-contain active:cursor-grabbing sm:h-11 sm:w-11"
                                      onError={(event) => {
                                        event.currentTarget.onerror =
                                          null;
                                        event.currentTarget.src =
                                          industryLogo;
                                      }}
                                    />

                                    <div className="min-w-0 flex-1">
                                      <div className="break-words text-[14px] font-medium leading-[1.25] text-[#365460] sm:text-[15px]">
                                        {experience.companyName}
                                      </div>

                                      {experience.duration && (
                                        <div className="mt-0.5 text-[12px] leading-[1.25] text-slate-500">
                                          {experience.duration}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <ul className="relative ml-[13px] mt-0.5 list-none pl-[26.5px]">
                                    {experience.subExperienceBlocks.map(
                                      (role, roleIndex) => (
                                        <li
                                          key={
                                            role.experienceID ||
                                            `${index}-${roleIndex}`
                                          }
                                          className={[
                                            "relative pl-3",
                                            roleIndex ===
                                            experience
                                              .subExperienceBlocks
                                              .length -
                                              1
                                              ? ""
                                              : "mb-1.5",
                                          ].join(" ")}
                                        >
                                          <span className="absolute -left-[20px] top-[6px] z-10 h-2 w-2 rounded-full bg-slate-400" />

                                          {roleIndex !==
                                            experience
                                              .subExperienceBlocks
                                              .length -
                                              1 && (
                                            <span className="absolute -left-[16.5px] top-[17px] z-0 h-[calc(78%)] w-px bg-slate-300" />
                                          )}

                                          <div className="break-words text-[14px] font-medium leading-[1.25] text-[#365460] sm:text-[15px]">
                                            {role.designation}
                                          </div>

                                          {(role.joiningDate ||
                                            role.leavingDate ||
                                            role.duration) && (
                                            <div className="mt-0.5 text-[11.5px] leading-[1.3] text-slate-500">
                                              {role.joiningDate}
                                              {role.leavingDate
                                                ? " - "
                                                : ""}
                                              {role.leavingDate}
                                              {role.duration
                                                ? " · "
                                                : ""}
                                              {role.duration}
                                            </div>
                                          )}

                                          {role.location && (
                                            <div className="mt-0.5 text-[11.5px] leading-[1.3] text-slate-500">
                                              {role.location}
                                            </div>
                                          )}

                                          {role.description && (
                                            <div className="mt-1 whitespace-pre-line text-[12px] leading-[1.35] text-slate-600">
                                              {role.description}
                                            </div>
                                          )}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </>
                              ) : (
                                <div className="flex items-start gap-3 pr-5">
                                  <img
                                    src={resolveExperienceLogo(
                                      experience.companyLogo
                                    )}
                                    alt="Company logo"
                                    draggable
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                    onDragStart={(event) =>
                                      handleExperienceLogoDragStart(
                                        event,
                                        experience.companyLogo
                                      )
                                    }
                                    className="h-10 w-10 shrink-0 cursor-grab rounded-full border border-slate-200 bg-white object-contain active:cursor-grabbing sm:h-11 sm:w-11"
                                    onError={(event) => {
                                      event.currentTarget.onerror =
                                        null;
                                      event.currentTarget.src =
                                        industryLogo;
                                    }}
                                  />

                                  <div className="min-w-0 flex-1">
                                    <div className="break-words text-[14px] font-medium leading-[1.25] text-[#365460] sm:text-[15px]">
                                      {experience.designation}
                                    </div>

                                    <div className="mt-0.5 break-words text-[13px] leading-[1.25] text-slate-600">
                                      {experience.companyName}
                                    </div>

                                    {(experience.joiningDate ||
                                      experience.leavingDate ||
                                      experience.duration) && (
                                      <div className="mt-0.5 text-[11.5px] leading-[1.3] text-slate-500">
                                        {experience.joiningDate}
                                        {experience.leavingDate
                                          ? " - "
                                          : ""}
                                        {experience.leavingDate}
                                        {experience.duration
                                          ? " · "
                                          : ""}
                                        {experience.duration}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <div className="flex h-full min-h-[120px] items-center justify-center text-center text-[13px] text-slate-400">
              No experience data available.
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-full items-center justify-center">
          <img
            className="h-6 w-6"
            src={LoadingIcon}
            alt="Loading..."
          />
        </div>
      )}

      <AnimatePresence mode="wait">
        {!readOnly && isModify && selectedExperience && (
          <motion.div
            key="modify-experience"
            initial={{ x: "-8%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-8%", opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-[80] overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-[0_12px_34px_rgba(15,23,42,0.16)]"
            data-pdp2-print-hide="true"
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#365561]">
                  Experience
                </div>
                <div className="text-[15px] font-semibold text-slate-900">
                  Modify experience
                </div>
              </div>

              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none"
                onClick={() => setIsModify(false)}
                aria-label="Close modify experience"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ModifyExperience
              experience={selectedExperience}
              getExpertExperience={getExpertExperience}
              setIsModify={setIsModify}
              clickedFrom="powerpoint"
            />
          </motion.div>
        )}

        {!readOnly && isNew && (
          <motion.div
            key="add-experience"
            initial={{ x: "-8%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-8%", opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-[80] overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-[0_12px_34px_rgba(15,23,42,0.16)]"
            data-pdp2-print-hide="true"
          >
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#365561]">
                  Experience
                </div>
                <div className="text-[15px] font-semibold text-slate-900">
                  Add new experience
                </div>
              </div>

              <button
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none"
                onClick={() => setIsNew(false)}
                aria-label="Close add experience"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <AddExperience
              expertID={expert_id}
              powerPointID={powerPointID}
              getExpertExperience={getExpertExperience}
              setIsNew={setIsNew}
              setIsModify={setIsModify}
              setSelectedExperience={setSelectedExperience}
              clickedFrom="powerpoint"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AddExperience({
  expertID,
  powerPointID,
  getExpertExperience,
  setIsNew,
  setIsModify,
  setSelectedExperience,
  clickedFrom,
}) {
  const { API_URL } = useContext(UrlContext);

  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [isMultiPosition, setIsMultiPosition] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [singleExperience, setSingleExperience] = useState({
    designation: "",
    joiningMonth: "",
    joiningYear: "",
    leavingMonth: "",
    leavingYear: "",
    isCurrent: false,
    jobType: "",
  });

  const [subExperienceBlocks, setSubExperienceBlocks] = useState([]);

  const addSub = () => {
    setSubExperienceBlocks((previous) => [
      ...previous,
      {
        designation: "",
        joiningMonth: "",
        joiningYear: "",
        leavingMonth: "",
        leavingYear: "",
        isCurrent: false,
        jobType: "",
      },
    ]);
  };

  const deleteSub = (index) => {
    setSubExperienceBlocks((previous) =>
      previous.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const handleSubChange = (index, field, value) => {
    setSubExperienceBlocks((previous) =>
      previous.map((block, currentIndex) =>
        currentIndex === index
          ? {
              ...block,
              [field]: value,
            }
          : block
      )
    );
  };

  const handleMultiToggle = () => {
    setError("");

    if (!isMultiPosition) {
      setSubExperienceBlocks([
        {
          ...singleExperience,
        },
        {
          designation: "",
          joiningMonth: "",
          joiningYear: "",
          leavingMonth: "",
          leavingYear: "",
          isCurrent: false,
          jobType: "",
        },
      ]);
    } else {
      const firstBlock = subExperienceBlocks[0] || {};

      setSingleExperience((previous) => ({
        ...previous,
        designation: firstBlock.designation || "",
        joiningMonth: firstBlock.joiningMonth || "",
        joiningYear: firstBlock.joiningYear || "",
        leavingMonth: firstBlock.leavingMonth || "",
        leavingYear: firstBlock.leavingYear || "",
        isCurrent: Boolean(firstBlock.isCurrent),
        jobType: firstBlock.jobType || "",
      }));

      setSubExperienceBlocks([]);
    }

    setIsMultiPosition((previous) => !previous);
  };

  const toggleSubCurrent = (index) => {
    setSubExperienceBlocks((previous) =>
      previous.map((block, currentIndex) =>
        currentIndex === index
          ? {
              ...block,
              isCurrent: !block.isCurrent,
              leavingMonth: !block.isCurrent
                ? ""
                : block.leavingMonth,
              leavingYear: !block.isCurrent
                ? ""
                : block.leavingYear,
            }
          : block
      )
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError("");

    const singleJoin = buildExperienceDate(
      singleExperience.joiningMonth,
      singleExperience.joiningYear
    );

    const singleLeave = singleExperience.isCurrent
      ? "Present"
      : buildExperienceDate(
          singleExperience.leavingMonth,
          singleExperience.leavingYear
        );

    const payload = isMultiPosition
      ? subExperienceBlocks.map((block) => {
          const joiningDate = buildExperienceDate(
            block.joiningMonth,
            block.joiningYear
          );
          const leavingDate = block.isCurrent
            ? "Present"
            : buildExperienceDate(
                block.leavingMonth,
                block.leavingYear
              );

          return {
            ...block,
            joiningDate,
            leavingDate,
            duration: calculateDurationFromDates(
              joiningDate,
              leavingDate
            ),
          };
        })
      : [];

    const firstBlock = payload[0] || {};
    const topLevelJoin = isMultiPosition
      ? firstBlock.joiningDate || ""
      : singleJoin;
    const topLevelLeave = isMultiPosition
      ? firstBlock.leavingDate || ""
      : singleLeave;

    const requestBody = {
      expertID,
      powerPointID,
      companyName,
      companyLogo,
      isManual: true,
      isCurrent: isMultiPosition
        ? payload.some((block) => block.isCurrent)
        : singleExperience.isCurrent,
      subExperienceBlocks: isMultiPosition ? payload : "",
      designation: isMultiPosition
        ? firstBlock.designation || ""
        : singleExperience.designation,
      joiningDate: topLevelJoin,
      leavingDate: topLevelLeave,
      jobType: isMultiPosition
        ? firstBlock.jobType || ""
        : singleExperience.jobType,
      duration: isMultiPosition
        ? calculateOverallDuration(payload)
        : calculateDurationFromDates(singleJoin, singleLeave),
    };

    try {
      const modifiedUrl =
        clickedFrom === "profile"
          ? "add-new-experience-block"
          : "add-new-power-point-experience-block";

      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        await getExpertExperience();
        setIsNew(false);
        setIsModify(false);
        setSelectedExperience?.(null);
      } else {
        Swal.fire({
          title: "Error!",
          text: responseData.text || "Something went wrong",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: String(error),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-[13px] text-slate-600">
      <div className="flex items-start gap-3">
        <ExperienceCompanyLogoEditor
          value={companyLogo}
          onChange={setCompanyLogo}
        />

        <div className="min-w-0 flex-1">
          <input
            className="mb-2 w-full border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-[14px] font-medium text-slate-800 outline-none transition hover:border-[#365561] focus:border-[#365561]"
            placeholder="Company name"
            value={companyName}
            onChange={(event) =>
              setCompanyName(event.target.value)
            }
          />

          <label className="mb-2 inline-flex cursor-pointer items-center gap-2 text-[11px] text-slate-500">
            <input
              type="checkbox"
              checked={isMultiPosition}
              onChange={handleMultiToggle}
              className="h-3.5 w-3.5 cursor-pointer accent-[#365561]"
            />
            <span>This role had multiple positions</span>
          </label>

          {!isMultiPosition && (
            <>
              <input
                className="mb-2 w-full border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-[14px] font-medium text-slate-800 outline-none transition hover:border-[#365561] focus:border-[#365561]"
                placeholder="Designation"
                value={singleExperience.designation}
                onChange={(event) =>
                  setSingleExperience((previous) => ({
                    ...previous,
                    designation: event.target.value,
                  }))
                }
              />

              <DateRangeFields
                joiningMonth={singleExperience.joiningMonth}
                joiningYear={singleExperience.joiningYear}
                leavingMonth={singleExperience.leavingMonth}
                leavingYear={singleExperience.leavingYear}
                isCurrent={singleExperience.isCurrent}
                onChange={(field, value) =>
                  setSingleExperience((previous) => ({
                    ...previous,
                    [field]: value,
                  }))
                }
                onToggleCurrent={() =>
                  setSingleExperience((previous) => ({
                    ...previous,
                    isCurrent: !previous.isCurrent,
                    leavingMonth: !previous.isCurrent
                      ? ""
                      : previous.leavingMonth,
                    leavingYear: !previous.isCurrent
                      ? ""
                      : previous.leavingYear,
                  }))
                }
              />

              <div className="mt-1 text-[11px] font-medium text-slate-400">
                {calculateDurationFromDates(
                  buildExperienceDate(
                    singleExperience.joiningMonth,
                    singleExperience.joiningYear
                  ),
                  singleExperience.isCurrent
                    ? "Present"
                    : buildExperienceDate(
                        singleExperience.leavingMonth,
                        singleExperience.leavingYear
                      )
                ) || "Duration"}
              </div>
            </>
          )}
        </div>
      </div>

      {isMultiPosition && (
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-[13px] font-semibold text-slate-800">
                {companyName || "Company roles"}
              </div>
              <div className="text-[11px] text-slate-400">
                {calculateOverallDuration(
                  subExperienceBlocks.map((block) => ({
                    ...block,
                    joiningDate: buildExperienceDate(
                      block.joiningMonth,
                      block.joiningYear
                    ),
                    leavingDate: block.isCurrent
                      ? "Present"
                      : buildExperienceDate(
                          block.leavingMonth,
                          block.leavingYear
                        ),
                  }))
                ) || "Overall duration"}
              </div>
            </div>

            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-[#365561] transition hover:bg-[#365561]/10 focus:outline-none"
              onClick={addSub}
            >
              <Plus className="h-3.5 w-3.5" />
              Add role
            </button>
          </div>

          <ul className="relative ml-1 list-none space-y-3 pl-5">
            {subExperienceBlocks.map((block, index) => (
              <li key={index} className="relative">
                <span className="absolute -left-[18px] top-[8px] z-10 h-2 w-2 rounded-full bg-slate-400" />

                {index !== subExperienceBlocks.length - 1 && (
                  <span className="absolute -left-[15px] top-[18px] h-[calc(100%+8px)] w-px bg-slate-300" />
                )}

                <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <input
                        className="mb-1 w-full border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-[13px] font-medium text-slate-800 outline-none transition hover:border-[#365561] focus:border-[#365561]"
                        placeholder="Designation"
                        value={block.designation}
                        onChange={(event) =>
                          handleSubChange(
                            index,
                            "designation",
                            event.target.value
                          )
                        }
                      />

                      <DateRangeFields
                        joiningMonth={block.joiningMonth}
                        joiningYear={block.joiningYear}
                        leavingMonth={block.leavingMonth}
                        leavingYear={block.leavingYear}
                        isCurrent={block.isCurrent}
                        compact
                        onChange={(field, value) =>
                          handleSubChange(index, field, value)
                        }
                        onToggleCurrent={() =>
                          toggleSubCurrent(index)
                        }
                      />

                      <div className="mt-1 text-[10.5px] font-medium text-slate-400">
                        {calculateDurationFromDates(
                          buildExperienceDate(
                            block.joiningMonth,
                            block.joiningYear
                          ),
                          block.isCurrent
                            ? "Present"
                            : buildExperienceDate(
                                block.leavingMonth,
                                block.leavingYear
                              )
                        ) || "Duration"}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-rose-50 hover:text-rose-500 focus:outline-none"
                      onClick={() => deleteSub(index)}
                      aria-label="Remove role"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="mt-2 rounded-lg bg-rose-50 px-2 py-1.5 text-[11px] font-medium text-rose-600">
          {error}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
          onClick={() => {
            setIsNew(false);
            setIsModify(false);
            setSelectedExperience?.(null);
          }}
        >
          Cancel
        </button>

        <button
          type="button"
          disabled={saving}
          className="rounded-lg bg-[#365561] px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#2d4852] disabled:cursor-wait disabled:opacity-60"
          onClick={handleSubmit}
        >
          {saving ? "Saving..." : "Save experience"}
        </button>
      </div>
    </div>
  );
}

function ModifyExperience({
  experience,
  getExpertExperience,
  setIsModify,
  clickedFrom,
}) {
  const { API_URL } = useContext(UrlContext);

  const initialSingleStart = splitExperienceDate(
    experience.joiningDate
  );
  const initialSingleEnd = splitExperienceDate(
    experience.leavingDate
  );

  const initialSubBlocks = Array.isArray(
    experience.subExperienceBlocks
  )
    ? experience.subExperienceBlocks.map((block) => {
        const start = splitExperienceDate(block.joiningDate);
        const end = splitExperienceDate(block.leavingDate);

        return {
          ...block,
          joiningMonth: start.month,
          joiningYear: start.year,
          leavingMonth: end.month,
          leavingYear: end.year,
          isCurrent:
            end.isCurrent ||
            !block.leavingDate ||
            ["present", "heute"].includes(
              String(block.leavingDate).toLowerCase()
            ),
        };
      })
    : [];

  const [companyName, setCompanyName] = useState(
    experience.companyName || ""
  );
  const [companyLogo, setCompanyLogo] = useState(
    experience.companyLogo || ""
  );
  const [isMultiPosition, setIsMultiPosition] = useState(
    initialSubBlocks.length > 0
  );
  const [subExperienceBlocks, setSubExperienceBlocks] =
    useState(initialSubBlocks);
  const [singleExperience, setSingleExperience] = useState({
    designation: experience.designation || "",
    joiningMonth: initialSingleStart.month,
    joiningYear: initialSingleStart.year,
    leavingMonth: initialSingleEnd.month,
    leavingYear: initialSingleEnd.year,
    isCurrent:
      initialSingleEnd.isCurrent ||
      !experience.leavingDate ||
      ["present", "heute"].includes(
        String(experience.leavingDate).toLowerCase()
      ),
    jobType: experience.jobType || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubChange = (index, field, value) => {
    setSubExperienceBlocks((previous) =>
      previous.map((block, currentIndex) =>
        currentIndex === index
          ? {
              ...block,
              [field]: value,
            }
          : block
      )
    );
  };

  const toggleSubCurrent = (index) => {
    setSubExperienceBlocks((previous) =>
      previous.map((block, currentIndex) =>
        currentIndex === index
          ? {
              ...block,
              isCurrent: !block.isCurrent,
              leavingMonth: !block.isCurrent
                ? ""
                : block.leavingMonth,
              leavingYear: !block.isCurrent
                ? ""
                : block.leavingYear,
            }
          : block
      )
    );
  };

  const addSub = () => {
    setSubExperienceBlocks((previous) => [
      ...previous,
      {
        experienceID: null,
        expertExperienceID: experience.experienceID,
        designation: "",
        joiningMonth: "",
        joiningYear: "",
        leavingMonth: "",
        leavingYear: "",
        isCurrent: false,
        jobType: "",
      },
    ]);
  };

  const deleteSub = (index) => {
    const remaining = subExperienceBlocks.filter(
      (_, currentIndex) => currentIndex !== index
    );

    if (remaining.length === 1) {
      const first = remaining[0];

      setSingleExperience((previous) => ({
        ...previous,
        designation: first.designation || "",
        joiningMonth: first.joiningMonth || "",
        joiningYear: first.joiningYear || "",
        leavingMonth: first.leavingMonth || "",
        leavingYear: first.leavingYear || "",
        isCurrent: Boolean(first.isCurrent),
        jobType: first.jobType || "",
      }));

      setIsMultiPosition(false);
      setSubExperienceBlocks([]);
      return;
    }

    setSubExperienceBlocks(remaining);
  };

  const handleMultiToggle = () => {
    if (!isMultiPosition) {
      setSubExperienceBlocks([
        {
          experienceID: null,
          expertExperienceID: experience.experienceID,
          ...singleExperience,
        },
        {
          experienceID: null,
          expertExperienceID: experience.experienceID,
          designation: "",
          joiningMonth: "",
          joiningYear: "",
          leavingMonth: "",
          leavingYear: "",
          isCurrent: false,
          jobType: "",
        },
      ]);
    } else {
      const first = subExperienceBlocks[0] || {};

      setSingleExperience((previous) => ({
        ...previous,
        designation: first.designation || "",
        joiningMonth: first.joiningMonth || "",
        joiningYear: first.joiningYear || "",
        leavingMonth: first.leavingMonth || "",
        leavingYear: first.leavingYear || "",
        isCurrent: Boolean(first.isCurrent),
        jobType: first.jobType || "",
      }));

      setSubExperienceBlocks([]);
    }

    setIsMultiPosition((previous) => !previous);
  };

  const handleUpdate = async () => {
    setSaving(true);

    const singleJoin = buildExperienceDate(
      singleExperience.joiningMonth,
      singleExperience.joiningYear
    );

    const singleLeave = singleExperience.isCurrent
      ? "Present"
      : buildExperienceDate(
          singleExperience.leavingMonth,
          singleExperience.leavingYear
        );

    const payload = isMultiPosition
      ? subExperienceBlocks.map((block) => {
          const joiningDate = buildExperienceDate(
            block.joiningMonth,
            block.joiningYear
          );

          const leavingDate = block.isCurrent
            ? "Present"
            : buildExperienceDate(
                block.leavingMonth,
                block.leavingYear
              );

          return {
            ...block,
            joiningDate,
            leavingDate,
            duration: calculateDurationFromDates(
              joiningDate,
              leavingDate
            ),
          };
        })
      : [];

    const firstBlock = payload[0] || {};

    const updatedExperience = {
      experienceID: experience.experienceID,
      companyName,
      companyLogo,
      subExperienceBlocks: payload,
      designation: isMultiPosition
        ? firstBlock.designation || ""
        : singleExperience.designation,
      joiningDate: isMultiPosition
        ? firstBlock.joiningDate || ""
        : singleJoin,
      leavingDate: isMultiPosition
        ? firstBlock.leavingDate || ""
        : singleLeave,
      duration: isMultiPosition
        ? calculateOverallDuration(payload)
        : calculateDurationFromDates(singleJoin, singleLeave),
      jobType: isMultiPosition
        ? firstBlock.jobType || ""
        : singleExperience.jobType,
      isCurrent: isMultiPosition
        ? payload.some((block) => block.isCurrent)
        : singleExperience.isCurrent,
    };

    try {
      const modifiedUrl =
        clickedFrom === "profile"
          ? "update-expert-experience"
          : "update-power-point-experience";

      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          expertExperience: updatedExperience,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        await getExpertExperience();
        setIsModify(false);
      } else {
        Swal.fire({
          title: "Error!",
          text:
            responseData.text ||
            "Failed to update expert experience",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: String(error),
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteExperience = async () => {
    const result = await Swal.fire({
      title: "Delete selected experience block?",
      text: "Selected data will be removed permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const modifiedUrl =
        clickedFrom === "profile"
          ? "delete-expert-experience"
          : "delete-power-point-experience";

      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          experienceID: experience.experienceID,
        }),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        await getExpertExperience();
        setIsModify(false);

        Swal.fire({
          title: "Success!",
          text: "Experience has been deleted!",
          icon: "success",
          showConfirmButton: false,
          timer: 1200,
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: responseData.text,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Unexpected error!",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div className="text-[13px] text-slate-600">
      <div className="flex items-start gap-3">
        <ExperienceCompanyLogoEditor
          value={companyLogo}
          onChange={setCompanyLogo}
        />

        <div className="min-w-0 flex-1">
          <input
            className="mb-2 w-full border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-[14px] font-medium text-slate-800 outline-none transition hover:border-[#365561] focus:border-[#365561]"
            placeholder="Company name"
            value={companyName}
            onChange={(event) =>
              setCompanyName(event.target.value)
            }
          />

          <label className="mb-2 inline-flex cursor-pointer items-center gap-2 text-[11px] text-slate-500">
            <input
              type="checkbox"
              checked={isMultiPosition}
              onChange={handleMultiToggle}
              className="h-3.5 w-3.5 cursor-pointer accent-[#365561]"
            />
            <span>This role had multiple positions</span>
          </label>

          {!isMultiPosition && (
            <>
              <input
                className="mb-2 w-full border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-[14px] font-medium text-slate-800 outline-none transition hover:border-[#365561] focus:border-[#365561]"
                placeholder="Designation"
                value={singleExperience.designation}
                onChange={(event) =>
                  setSingleExperience((previous) => ({
                    ...previous,
                    designation: event.target.value,
                  }))
                }
              />

              <DateRangeFields
                joiningMonth={singleExperience.joiningMonth}
                joiningYear={singleExperience.joiningYear}
                leavingMonth={singleExperience.leavingMonth}
                leavingYear={singleExperience.leavingYear}
                isCurrent={singleExperience.isCurrent}
                onChange={(field, value) =>
                  setSingleExperience((previous) => ({
                    ...previous,
                    [field]: value,
                  }))
                }
                onToggleCurrent={() =>
                  setSingleExperience((previous) => ({
                    ...previous,
                    isCurrent: !previous.isCurrent,
                    leavingMonth: !previous.isCurrent
                      ? ""
                      : previous.leavingMonth,
                    leavingYear: !previous.isCurrent
                      ? ""
                      : previous.leavingYear,
                  }))
                }
              />

              <div className="mt-1 text-[11px] font-medium text-slate-400">
                {calculateDurationFromDates(
                  buildExperienceDate(
                    singleExperience.joiningMonth,
                    singleExperience.joiningYear
                  ),
                  singleExperience.isCurrent
                    ? "Present"
                    : buildExperienceDate(
                        singleExperience.leavingMonth,
                        singleExperience.leavingYear
                      )
                ) || "Duration"}
              </div>
            </>
          )}
        </div>
      </div>

      {isMultiPosition && (
        <div className="mt-3">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-[13px] font-semibold text-slate-800">
                {companyName || "Company roles"}
              </div>
              <div className="text-[11px] text-slate-400">
                {calculateOverallDuration(
                  subExperienceBlocks.map((block) => ({
                    ...block,
                    joiningDate: buildExperienceDate(
                      block.joiningMonth,
                      block.joiningYear
                    ),
                    leavingDate: block.isCurrent
                      ? "Present"
                      : buildExperienceDate(
                          block.leavingMonth,
                          block.leavingYear
                        ),
                  }))
                ) || "Overall duration"}
              </div>
            </div>

            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-[#365561] transition hover:bg-[#365561]/10 focus:outline-none"
              onClick={addSub}
            >
              <Plus className="h-3.5 w-3.5" />
              Add role
            </button>
          </div>

          <ul className="relative ml-1 list-none space-y-3 pl-5">
            {subExperienceBlocks.map((block, index) => (
              <li
                key={block.experienceID || index}
                className="relative"
              >
                <span className="absolute -left-[18px] top-[8px] z-10 h-2 w-2 rounded-full bg-slate-400" />

                {index !== subExperienceBlocks.length - 1 && (
                  <span className="absolute -left-[15px] top-[18px] h-[calc(100%+8px)] w-px bg-slate-300" />
                )}

                <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-2">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <input
                        className="mb-1 w-full border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-[13px] font-medium text-slate-800 outline-none transition hover:border-[#365561] focus:border-[#365561]"
                        placeholder="Designation"
                        value={block.designation || ""}
                        onChange={(event) =>
                          handleSubChange(
                            index,
                            "designation",
                            event.target.value
                          )
                        }
                      />

                      <DateRangeFields
                        joiningMonth={block.joiningMonth}
                        joiningYear={block.joiningYear}
                        leavingMonth={block.leavingMonth}
                        leavingYear={block.leavingYear}
                        isCurrent={block.isCurrent}
                        compact
                        onChange={(field, value) =>
                          handleSubChange(index, field, value)
                        }
                        onToggleCurrent={() =>
                          toggleSubCurrent(index)
                        }
                      />

                      <div className="mt-1 text-[10.5px] font-medium text-slate-400">
                        {calculateDurationFromDates(
                          buildExperienceDate(
                            block.joiningMonth,
                            block.joiningYear
                          ),
                          block.isCurrent
                            ? "Present"
                            : buildExperienceDate(
                                block.leavingMonth,
                                block.leavingYear
                              )
                        ) || "Duration"}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-rose-50 hover:text-rose-500 focus:outline-none"
                      onClick={() => deleteSub(index)}
                      aria-label="Remove role"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold text-rose-600 transition hover:bg-rose-50 focus:outline-none"
          onClick={deleteExperience}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </button>

        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
            onClick={() => setIsModify(false)}
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={saving}
            className="rounded-lg bg-[#365561] px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#2d4852] disabled:cursor-wait disabled:opacity-60"
            onClick={handleUpdate}
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DateRangeFields({
  joiningMonth,
  joiningYear,
  leavingMonth,
  leavingYear,
  isCurrent,
  onChange,
  onToggleCurrent,
  compact = false,
}) {
  const selectClass = [
    "min-w-0 border-0 border-b border-slate-300 bg-transparent px-0 py-1 text-slate-600 outline-none transition hover:border-[#365561] focus:border-[#365561]",
    compact ? "text-[10.5px]" : "text-[11.5px]",
  ].join(" ");

  return (
    <>
      <div className="grid grid-cols-[minmax(0,.72fr)_minmax(0,1fr)_10px_minmax(0,.72fr)_minmax(0,1fr)] items-center gap-1">
        <select
          className={selectClass}
          value={joiningMonth || ""}
          onChange={(event) =>
            onChange("joiningMonth", event.target.value)
          }
          title="Start month — optional"
        >
          <option value="">Month</option>
          {MONTHS.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>

        <select
          className={selectClass}
          value={joiningYear || ""}
          onChange={(event) =>
            onChange("joiningYear", event.target.value)
          }
        >
          <option value="">Year</option>
          {YEARS.map((year) => (
            <option key={year} value={String(year)}>
              {year}
            </option>
          ))}
        </select>

        <span className="text-center text-slate-400">-</span>

        {isCurrent ? (
          <span className="col-span-2 text-[11px] text-slate-400">
            Present
          </span>
        ) : (
          <>
            <select
              className={selectClass}
              value={leavingMonth || ""}
              onChange={(event) =>
                onChange("leavingMonth", event.target.value)
              }
              title="End month — optional"
            >
              <option value="">Month</option>
              {MONTHS.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>

            <select
              className={selectClass}
              value={leavingYear || ""}
              onChange={(event) =>
                onChange("leavingYear", event.target.value)
              }
            >
              <option value="">Year</option>
              {YEARS.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <label className="mt-1.5 inline-flex cursor-pointer items-center gap-1.5 text-[10.5px] text-slate-500">
        <input
          type="checkbox"
          checked={Boolean(isCurrent)}
          onChange={onToggleCurrent}
          className="h-3 w-3 cursor-pointer accent-[#365561]"
        />
        <span>Current position</span>
      </label>
    </>
  );
}
